import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Flex, Text, Table, Thead, Tbody, Tr, Th, Td, IconButton, Input, InputGroup,
    HStack, Select, Spinner, Badge, InputLeftElement
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { getLogAcessos, getUsuarios } from "../services/api";
import { useDebounce } from "../hooks/useDebounce";
import { semBloqueados } from "../hooks/useUsuarios";
import { ChevronLeft, ChevronRight, Globe, Monitor, MapPin, Smartphone, Tablet } from "lucide-react";

const DeviceIcon = ({ tipo }) => {
    if (tipo === "Mobile") return <Smartphone size={13} />;
    if (tipo === "Tablet") return <Tablet size={13} />;
    return <Monitor size={13} />;
};

const LogAcessos = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterUser, setFilterUser] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationMeta, setPaginationMeta] = useState(null);
    const [usuarios, setUsuarios] = useState([]);
    const debouncedSearch = useDebounce(searchTerm, 500);

    // Carrega lista de usuarios para o filtro
    useEffect(() => {
        const load = async () => {
            const res = await getUsuarios({ limit: 100 });
            if (res && res.data) setUsuarios(res.data);
        };
        load();
    }, []);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        const params = { page: currentPage, limit: 20 };
        if (debouncedSearch) params.search = debouncedSearch;
        if (filterUser) params.user_id = filterUser;
        const res = await getLogAcessos(params);
        if (res) {
            setLogs(res.data);
            setPaginationMeta({ current_page: res.current_page, last_page: res.last_page, total: res.total });
        }
        setLoading(false);
    }, [currentPage, debouncedSearch, filterUser]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);
    useEffect(() => { setCurrentPage(1); }, [debouncedSearch, filterUser]);

    const formatDate = (dt) => {
        const d = new Date(dt);
        return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
            + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    };

    const ml = { base: "0", md: "64px" };
    const px = { base: 3, md: 6 };

    return (
        <Box ml={ml} p={px} pt="75px" minH="100vh" bg="gray.50">
            {/* Header */}
            <Box mb={4}>
                <Text fontSize="xl" fontWeight="bold" color="gray.800">Gestão de Acessos</Text>
                <Text fontSize="sm" color="gray.500">
                    Histórico de logins no sistema
                    {paginationMeta && ` — ${paginationMeta.total} registro(s)`}
                </Text>
            </Box>

            {/* Filters */}
            <Flex mb={4} gap={3} flexWrap="wrap">
                <InputGroup maxW="300px" size="sm">
                    <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                    <Input placeholder="Buscar por nome, IP ou cidade..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} bg="white" borderRadius="lg" />
                </InputGroup>
                <Select maxW="220px" size="sm" bg="white" borderRadius="lg" value={filterUser} onChange={(e) => setFilterUser(e.target.value)} placeholder="Todos os usuários">
                    {semBloqueados(usuarios).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </Select>
            </Flex>

            {/* Table */}
            <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
                {loading ? (
                    <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>
                ) : logs.length === 0 ? (
                    <Text textAlign="center" py={10} color="gray.400" fontSize="sm">Nenhum registro encontrado</Text>
                ) : (
                    <Box overflowX="auto">
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th fontSize="xs" color="gray.500">Data/Hora</Th>
                                    <Th fontSize="xs" color="gray.500">Usuário</Th>
                                    <Th fontSize="xs" color="gray.500">IP</Th>
                                    <Th fontSize="xs" color="gray.500">Dispositivo</Th>
                                    <Th fontSize="xs" color="gray.500">Navegador</Th>
                                    <Th fontSize="xs" color="gray.500">SO</Th>
                                    <Th fontSize="xs" color="gray.500">Localização</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {logs.map((log) => (
                                    <Tr key={log.id} _hover={{ bg: "gray.50" }}>
                                        <Td fontSize="sm" whiteSpace="nowrap">
                                            <Text fontWeight="medium" color="gray.800">{formatDate(log.created_at)}</Text>
                                        </Td>
                                        <Td fontSize="sm" fontWeight="medium">{log.user_name}</Td>
                                        <Td fontSize="xs" fontFamily="mono" color="gray.600">
                                            <HStack spacing={1}>
                                                <Globe size={12} />
                                                <Text>{log.ip || "—"}</Text>
                                            </HStack>
                                        </Td>
                                        <Td>
                                            <Badge
                                                colorScheme={log.dispositivo === "Mobile" ? "blue" : log.dispositivo === "Tablet" ? "purple" : "gray"}
                                                fontSize="10px" borderRadius="md" px={2}
                                            >
                                                <HStack spacing={1}>
                                                    <DeviceIcon tipo={log.dispositivo} />
                                                    <Text>{log.dispositivo || "—"}</Text>
                                                </HStack>
                                            </Badge>
                                        </Td>
                                        <Td fontSize="xs" color="gray.600">{log.navegador || "—"}</Td>
                                        <Td fontSize="xs" color="gray.600">{log.sistema_operacional || "—"}</Td>
                                        <Td fontSize="xs" color="gray.500">
                                            {(log.cidade || log.regiao || log.pais) ? (
                                                <HStack spacing={1}>
                                                    <MapPin size={11} />
                                                    <Text>{[log.cidade, log.regiao].filter(Boolean).join(", ")}</Text>
                                                </HStack>
                                            ) : "—"}
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                )}

                {/* Pagination */}
                {paginationMeta && paginationMeta.last_page > 1 && (
                    <Flex justify="space-between" align="center" px={4} py={3} borderTop="1px solid" borderColor="gray.100">
                        <Text fontSize="xs" color="gray.500">Página {paginationMeta.current_page} de {paginationMeta.last_page}</Text>
                        <HStack spacing={2}>
                            <IconButton icon={<ChevronLeft size={16} />} size="xs" variant="outline" isDisabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} aria-label="Anterior" />
                            <IconButton icon={<ChevronRight size={16} />} size="xs" variant="outline" isDisabled={currentPage >= paginationMeta.last_page} onClick={() => setCurrentPage(p => p + 1)} aria-label="Próxima" />
                        </HStack>
                    </Flex>
                )}
            </Box>
        </Box>
    );
};

export default LogAcessos;
